import {isClientInteractionEnabled} from '@github-ui/ahp-relay/relay-command-observation'
import {useEffect, useMemo} from 'react'

import type {CopilotClientInteractionContext} from './copilot-client-interaction'
import {
  createCopilotClientInteractionObserver,
  createQueuedClientInteractionObserver,
} from './copilot-client-interaction'
import {useCopilotUiReadiness} from './copilot-ui-readiness-context'
import {useOneCopilotBrowserTelemetryEnabled} from './use-one-copilot-browser-telemetry-enabled'

export function useCopilotClientInteractions({
  surface,
  role,
  session_id,
  environment_id,
  task_id,
  chat_id,
  client_view_id,
}: CopilotClientInteractionContext) {
  const enabled = useOneCopilotBrowserTelemetryEnabled(isClientInteractionEnabled())
  const readinessSurface = useCopilotUiReadiness()?.open.surface
  const resolvedSurface = surface && surface !== 'unknown' ? surface : (readinessSurface ?? surface)
  return useMemo(
    () =>
      enabled
        ? createCopilotClientInteractionObserver({
            surface: resolvedSurface,
            role,
            session_id,
            environment_id,
            task_id,
            chat_id,
            client_view_id,
          })
        : undefined,
    [enabled, resolvedSurface, role, session_id, environment_id, task_id, chat_id, client_view_id],
  )
}

export function useQueuedClientInteractions({
  surface,
  role,
  session_id,
  environment_id,
  task_id,
  chat_id,
  client_view_id,
}: CopilotClientInteractionContext) {
  const enabled = useOneCopilotBrowserTelemetryEnabled(isClientInteractionEnabled())
  const readinessSurface = useCopilotUiReadiness()?.open.surface
  const resolvedSurface = surface && surface !== 'unknown' ? surface : (readinessSurface ?? surface)
  const observer = useMemo(
    () =>
      enabled
        ? createQueuedClientInteractionObserver({
            surface: resolvedSurface,
            role,
            session_id,
            environment_id,
            task_id,
            chat_id,
            client_view_id,
          })
        : undefined,
    [enabled, resolvedSurface, role, session_id, environment_id, task_id, chat_id, client_view_id],
  )
  useEffect(() => () => observer?.dispose(), [observer])
  return observer?.begin
}
